import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import {
  ApiResponse,
  IAuthResponse,
  ILoginRequest,
  IRefreshResponse,
  IRegisterRequest,
  IUserResponse,
} from "@balizahub/shared";
import { firstValueFrom } from "rxjs";
import {
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from "../../features/auth/models/auth.model";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = "auth";

  // ── Private state ──────────────────────────────────────────
  private readonly _accessToken = signal<string | null>(null);
  private readonly _currentUser = signal<IUserResponse | null>(null);
  private readonly _isLoading = signal(false);

  // ── Public readonly signals ────────────────────────────────
  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._accessToken() !== null);
  readonly isLoading = this._isLoading.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();

  private unwrap<T>(payload: T | ApiResponse<T>): T {
    if (payload && typeof payload === "object" && "data" in payload) {
      return (payload as ApiResponse<T>).data;
    }
    return payload as T;
  }

  // ── Auth operations ────────────────────────────────────────

  async login(request: ILoginRequest): Promise<void> {
    this._isLoading.set(true);
    try {
      const raw = await firstValueFrom(
        this.http.post<IAuthResponse | ApiResponse<IAuthResponse>>(
          `${this.apiUrl}/login`,
          request,
          {
            withCredentials: true,
          },
        ),
      );
      const { accessToken, user } = this.unwrap(raw);
      this._accessToken.set(accessToken);
      this._currentUser.set(user);
    } finally {
      this._isLoading.set(false);
    }
  }

  async register(request: IRegisterRequest): Promise<void> {
    this._isLoading.set(true);
    try {
      const raw = await firstValueFrom(
        this.http.post<IAuthResponse | ApiResponse<IAuthResponse>>(
          `${this.apiUrl}/register`,
          request,
          {
            withCredentials: true,
          },
        ),
      );
      const { accessToken, user } = this.unwrap(raw);
      this._accessToken.set(accessToken);
      this._currentUser.set(user);
    } finally {
      this._isLoading.set(false);
    }
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<void | ApiResponse<void>>(
          `${this.apiUrl}/logout`,
          {},
          { withCredentials: true },
        ),
      );
    } finally {
      this._accessToken.set(null);
      this._currentUser.set(null);
    }
  }

  async refreshToken(): Promise<string | null> {
    try {
      const raw = await firstValueFrom(
        this.http.post<IRefreshResponse | ApiResponse<IRefreshResponse>>(
          `${this.apiUrl}/refresh`,
          {},
          { withCredentials: true },
        ),
      );
      const { accessToken } = this.unwrap(raw);
      this._accessToken.set(accessToken);
      // Keep user state in sync with refreshed access tokens.
      await this.loadCurrentUser();
      return accessToken;
    } catch {
      this._accessToken.set(null);
      this._currentUser.set(null);
      return null;
    }
  }

  async loadCurrentUser(): Promise<void> {
    try {
      const raw = await firstValueFrom(
        this.http.get<IUserResponse | ApiResponse<IUserResponse>>(
          `${this.apiUrl}/me`,
          {
            withCredentials: true,
          },
        ),
      );
      const user = this.unwrap(raw);
      this._currentUser.set(user);
    } catch {
      this._currentUser.set(null);
    }
  }

  async initializeSession(): Promise<void> {
    if (this._accessToken() !== null && this._currentUser() !== null) {
      return;
    }

    await this.refreshToken();
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<void> {
    await firstValueFrom(
      this.http.post<void | ApiResponse<void>>(
        `${this.apiUrl}/forgot-password`,
        request,
      ),
    );
  }

  async resetPassword(request: ResetPasswordRequest): Promise<void> {
    await firstValueFrom(
      this.http.post<void | ApiResponse<void>>(
        `${this.apiUrl}/reset-password`,
        request,
      ),
    );
  }

  /** Redirect to backend Google OAuth — integração real pós-MVP */
  loginWithGoogle(): void {
    window.location.href = "/api/auth/google";
  }
}
